import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePricingConfigs } from '../../../hooks/useApi';
import { cn } from '../../../lib/utils';
import { AdminActiveStatusBadge } from './AdminActiveStatusBadge';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { formFieldGroupSurfaceClassName } from '../../ui/formPrimitives';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import {
    formatPricingConfigActivitiesSummary,
    formatPricingConfigDateTime,
    isPricingConfigPayload,
} from '../adminDashboardHelpers';
import { useAdminBreadcrumbBack } from '../../../hooks/useAdminBreadcrumbBack';

function DetailField({
    label,
    children,
    className,
}: {
    label: string;
    children: ReactNode;
    className?: string;
}) {
    return (
        <div className={cn(className)}>
            <p className="text-sm text-neutral-500">{label}</p>
            <div className="mt-1 font-medium text-neutral-950">{children}</div>
        </div>
    );
}

export function PricingConfigDetail() {
    const { pricingConfigId } = useParams<{ pricingConfigId: string }>();
    const { goBack } = useAdminBreadcrumbBack();
    const pricingQuery = usePricingConfigs(true);
    const pricingConfig =
        pricingConfigId !== undefined
            ? pricingQuery.data?.pricingConfigs.find((c) => c.id === pricingConfigId)
            : undefined;

    if (pricingQuery.isLoading) {
        return (
            <Card>
                <CardContent className="p-8 text-sm text-neutral-500">
                    Loading pricing configuration…
                </CardContent>
            </Card>
        );
    }

    if (!pricingConfigId || !pricingConfig) {
        return (
            <Card>
                <CardContent className="space-y-4 p-8 text-sm text-neutral-600">
                    <p>Pricing configuration not found.</p>
                    <Button variant="outline" asChild>
                        <Link to="/admin">Return to credit admin</Link>
                    </Button>
                </CardContent>
            </Card>
        );
    }

    const payload = isPricingConfigPayload(pricingConfig.config) ? pricingConfig.config : null;
    const unitLabel = payload ? (payload.unitType === 'token' ? 'Token' : 'Credit') : null;
    const b2bCostLabel = payload?.unitType === 'token' ? 'B2B tokens' : 'B2B credits';
    const b2cCostLabel = payload?.unitType === 'token' ? 'B2C tokens' : 'B2C credits';

    return (
        <div className="space-y-6">
            <div className="flex items-start gap-3">
                <Button variant="ghost" size="icon" onClick={goBack}>
                    <ArrowLeft className="size-5" />
                </Button>
                <div>
                    <p className="text-sm text-neutral-500">Pricing configuration</p>
                    <h1 className="text-3xl font-semibold text-neutral-950">
                        {pricingConfig.name}
                    </h1>
                    <p className="mt-1 text-sm text-neutral-500">ID: {pricingConfig.id}</p>
                </div>
            </div>

            <Card>
                <CardContent className="grid gap-6 p-6 md:grid-cols-2">
                    <DetailField label="Version">{pricingConfig.version}</DetailField>
                    <DetailField label="Status">
                        <AdminActiveStatusBadge isActive={pricingConfig.isActive} />
                    </DetailField>
                    <DetailField label="Pricing configuration group ID" className="md:col-span-2">
                        <span className="font-mono text-sm font-normal">
                            {pricingConfig.pricingConfigGroupId}
                        </span>
                    </DetailField>
                    <DetailField label="Created">
                        {formatPricingConfigDateTime(pricingConfig.createdAt)}
                    </DetailField>
                    <DetailField label="Last updated">
                        {formatPricingConfigDateTime(pricingConfig.updatedAt)}
                    </DetailField>
                    {pricingConfig.description ? (
                        <DetailField label="Description" className="md:col-span-2">
                            <span className="font-normal">{pricingConfig.description}</span>
                        </DetailField>
                    ) : null}
                </CardContent>
            </Card>

            <Card>
                <CardContent className="space-y-6 p-6">
                    <div>
                        <h2 className="text-lg font-semibold text-neutral-950">
                            Pricing parameters
                        </h2>
                        <p className="mt-1 text-sm text-neutral-500">
                            Values stored in this version&apos;s JSON configuration (Pro + Pulse).
                        </p>
                    </div>

                    {payload ? (
                        <>
                            <DetailField label="Unit type">{unitLabel}</DetailField>

                            <section className={cn(formFieldGroupSurfaceClassName, 'space-y-4')}>
                                <div>
                                    <h3 className="text-sm font-semibold text-neutral-950">Pro</h3>
                                    <p className="text-xs text-neutral-600">
                                        Estimates, B2B/B2C costs, sentiment map, and sample-size
                                        multipliers.
                                    </p>
                                </div>
                                <DetailField label="Supports pricing estimates">
                                    {payload.pro.supportsPricingEstimates ? 'Yes' : 'No'}
                                </DetailField>
                                <div className="grid gap-4 md:grid-cols-3">
                                    <DetailField label="Sentiment map multiplier">
                                        {payload.pro.sentimentMapMultiplier}
                                    </DetailField>
                                    <DetailField label={b2bCostLabel}>
                                        {payload.pro.b2bCredits}
                                    </DetailField>
                                    <DetailField label={b2cCostLabel}>
                                        {payload.pro.b2cCredits}
                                    </DetailField>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-sm text-neutral-500">Sample sizes</p>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Sample size (n)</TableHead>
                                                <TableHead>Multiplier</TableHead>
                                                <TableHead />
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {[...payload.pro.sampleSizes]
                                                .slice()
                                                .sort((a, b) => a.n - b.n)
                                                .map((row) => {
                                                    const isDefault =
                                                        row.n === payload.pro.defaultSampleSize;
                                                    return (
                                                        <TableRow
                                                            key={row.n}
                                                            className={cn(
                                                                isDefault && 'bg-muted/35',
                                                            )}
                                                        >
                                                            <TableCell className="font-medium">
                                                                {row.n}
                                                            </TableCell>
                                                            <TableCell>{row.multiplier}</TableCell>
                                                            <TableCell className="text-muted-foreground">
                                                                {isDefault ? 'Default' : ''}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                    <p className="text-xs text-neutral-600">
                                        Default sample size (selected for new estimates):{' '}
                                        <span className="font-medium text-neutral-900">
                                            {payload.pro.defaultSampleSize}
                                        </span>
                                    </p>
                                </div>
                            </section>

                            <section className={cn(formFieldGroupSurfaceClassName, 'space-y-4')}>
                                <div>
                                    <h3 className="text-sm font-semibold text-neutral-950">
                                        Pulse
                                    </h3>
                                    <p className="text-xs text-neutral-600">
                                        Pricing inputs for standard Pulse usage.
                                    </p>
                                </div>
                                <DetailField label="Standard Pulse">
                                    {payload.pulse.standardPulse}
                                </DetailField>
                            </section>
                        </>
                    ) : (
                        <div className="space-y-4 rounded-md border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-900 dark:bg-amber-950/40">
                            <div>
                                <p className="text-sm font-medium text-amber-950 dark:text-amber-100">
                                    Legacy or non-standard configuration shape
                                </p>
                                <p className="mt-1 text-sm text-amber-900/90 dark:text-amber-200/90">
                                    Summary (may be incomplete):{' '}
                                    <span className="font-medium">
                                        {formatPricingConfigActivitiesSummary(pricingConfig.config)}
                                    </span>
                                </p>
                            </div>
                            <div>
                                <p className="text-xs font-medium uppercase tracking-wide text-amber-900/80 dark:text-amber-300/80">
                                    Raw JSON
                                </p>
                                <pre className="mt-2 max-h-96 overflow-auto rounded-md bg-white/90 p-3 font-mono text-xs text-neutral-800 ring-1 ring-inset ring-amber-200/80 dark:bg-neutral-950 dark:text-neutral-200 dark:ring-amber-900/60">
                                    {JSON.stringify(pricingConfig.config, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
